import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import './EventAnketoAIReport.css';

const AIEventReport = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState("");
    const [prompt, setPrompt] = useState("このイベントのアンケート結果を分析し、改善点を3つ提案してください。また、全体的な満足度を要約してください。");
    const [eventInfo, setEventInfo] = useState(null);
    // アンケート回答件数。null = 未取得（取得失敗を含む）
    const [responseCount, setResponseCount] = useState(null);
    const [countLoading, setCountLoading] = useState(true);

    useEffect(() => {
        // 情報を取得してイベント名を表示するためのAPI呼び出し
        api.get(`/events/${eventId}/`).then(res => setEventInfo(res.data));
    }, [eventId]);

    useEffect(() => {
        // 回答が 0 件だと分析対象が無いため、件数を取得してボタンの活性を決める
        let cancelled = false;
        setCountLoading(true);
        api.get(`/events/${eventId}/get_responses/`)
            .then(res => {
                if (cancelled) return;
                setResponseCount(Array.isArray(res.data) ? res.data.length : null);
            })
            .catch(err => {
                if (cancelled) return;
                console.error("アンケート回答件数の取得エラー:", err);
                setResponseCount(null);
            })
            .finally(() => {
                if (!cancelled) setCountLoading(false);
            });
        return () => { cancelled = true; };
    }, [eventId]);

    const hasNoResponse = responseCount === 0;
    const canGenerate = !loading && !countLoading && !hasNoResponse;

    const generateReport = async () => {
        setLoading(true);
        try {
            // APIエンドポイントは仮のものです。実際のエンドポイントに合わせてください。
            const response = await api.post(`/events/${eventId}/analyze_ai/`, { prompt });
            setReport(response.data.analysis);
        } catch (error) {
            alert("レポートの生成に失敗しました。もう一度お試しください。");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ai-report-page" style={{ background: '#f8fafc', minHeight: '100vh', padding: '40px 20px' }}>
            <div className="ai-report-card" style={{ maxWidth: '850px', margin: '0 auto', background: '#fff', padding: '50px', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                
                {/* Header */}
                <div style={{ borderBottom: '2px solid #1e40af', paddingBottom: '20px', marginBottom: '30px' }}>
                    <h1 style={{ fontSize: '1.8rem', color: '#1e40af', marginBottom: '10px' }}>
                        AI イベント分析レポート
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '1rem' }}>
                        イベント名：<strong>{eventInfo?.eventName || "読み込み中..."}</strong>
                    </p>
                    <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                        作成日：{new Date().toLocaleDateString('ja-JP')} | 作成者：イベント分析AI
                    </p>
                </div>

                {/* 分析指示 */}
                <div style={{ background: '#f1f5f9', padding: '20px', borderRadius: '8px', marginBottom: '30px' }} className="no-print">
                    <h5 style={{ marginBottom: '10px', fontSize: '1rem' }}>分析の指示（カスタムプロンプト）</h5>
                    <textarea 
                        style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', minHeight: '80px', marginBottom: '15px' }}
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                    />
                    <button 
                        onClick={generateReport}
                        disabled={!canGenerate}
                        title={hasNoResponse ? "アンケートの回答がないため分析できません" : undefined}
                        style={{
                            padding: '10px 20px',
                            cursor: canGenerate ? 'pointer' : 'not-allowed',
                            background: canGenerate ? '#334155' : '#cbd5e1',
                            color: canGenerate ? '#fff' : '#64748b',
                            border: 'none',
                            borderRadius: '4px',
                        }}
                    >
                        {loading ? "分析中..." : "レポートを生成する"}
                    </button>
                    {hasNoResponse && (
                        <p style={{ marginTop: '12px', marginBottom: 0, color: '#b45309', fontSize: '0.9rem' }}>
                            このイベントにはアンケートの回答がまだありません。回答が 1 件以上集まると分析できます。
                        </p>
                    )}
                </div>

                {/* レポート内容 */}
                {report ? (
                    <div style={{ lineHeight: '1.8', color: '#334155', fontSize: '1.05rem' }}>
                        <div
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report) }}
                            className="ai-report-content"
                        />
                        {/*}
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                            {report}
                        </div>
                        */}
                        {/* 印刷ボタン */}
                        <div style={{ marginTop: '40px', textAlign: 'right' }} className="no-print">
                            <button 
                                onClick={() => window.print()}
                                style={{ padding: '10px 20px', cursor: 'pointer', background: '#334155', color: '#fff', border: 'none', borderRadius: '4px' }}
                            >
                                🖨️ PDFとして保存 / 印刷
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                        {loading ? "AIがデータを解析してレポートを作成しています..." : "上のボタンを押して、AIレポートを生成してください。"}
                    </div>
                )}
            </div>

            {/* 戻るボタン */}
            <div style={{ textAlign: 'center', marginTop: '20px' }} className="no-print">
                <button 
                    onClick={() => navigate(-1)}
                    style={{ padding: '10px 20px', cursor: 'pointer', background: '#334155', color: '#fff', border: 'none', borderRadius: '4px' }}
                >
                    🠔 統計画面に戻る
                </button>
            </div>

            {/* 印刷用スタイルは EventAnketoAIReport.css の @media print に定義 */}
        </div>
    );
};

export default AIEventReport;